import { LegacyAuthAdapter } from './legacy/LegacyAuthAdapter';
import UserService from './legacy/UserService';
import { resolveAuthProvider } from './provider';
import type { AuthProvider } from './provider';
import type {
  AuthAccessTokenOptions,
  AuthAdapter,
  AuthLogoutOptions,
  AuthNotice,
  AuthProfileUpdatePayload,
  AuthStartLoginOptions,
  AuthState,
} from './types';
import { AuthV2Adapter } from './v2/AuthV2Adapter';

type AuthInitOptions = {
  adapter?: AuthAdapter;
  provider?: AuthProvider;
  endpoints?: {
    baseUri?: string;
  };
};

const defaultState: AuthState = {
  loading: true,
  authenticated: false,
  token: null,
  user: null,
};

class AuthClient {
  private adapter: AuthAdapter | null = null;

  private adapterUnsubscribe: (() => void) | null = null;

  private endpoints: AuthInitOptions['endpoints'];

  private listeners = new Set<() => void>();

  private provider: AuthProvider | null = null;

  init(options: AuthInitOptions = {}) {
    if (this.adapter) {
      throw new Error('Auth client has already been initialized.');
    }

    this.endpoints = options.endpoints;
    this.setAdapter(options.adapter ?? this.createAdapter(options), options.provider ?? resolveAuthProvider());
    return this;
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): AuthState {
    return this.adapter?.getState() ?? defaultState;
  }

  async startLogin(options?: AuthStartLoginOptions) {
    if (options?.provider && options.provider !== this.provider) {
      const adapter = this.createAdapter({ endpoints: this.endpoints, provider: options.provider });
      this.setAdapter(adapter, options.provider, { initialize: false });
      return adapter.startLogin(options);
    }
    return this.getAdapter().startLogin(options);
  }

  async completeLoginIfNeeded() {
    return this.getAdapter().completeLoginIfNeeded();
  }

  async getAccessToken(options?: AuthAccessTokenOptions) {
    return this.getAdapter().getAccessToken(options);
  }

  async getFreshAccessToken(options?: AuthAccessTokenOptions) {
    return this.getAdapter().getFreshAccessToken(options);
  }

  async logout(options?: AuthLogoutOptions) {
    return this.getAdapter().logout(options);
  }

  isAuthenticated() {
    return this.getAdapter().isAuthenticated();
  }

  async updateProfile(data: AuthProfileUpdatePayload) {
    await this.getFreshAccessToken();
    await this.updateCurrentProfile(data);
    await this.getAdapter().refreshSession();
  }

  consumeNotice(): AuthNotice | null {
    return this.getAdapter().consumeNotice?.() ?? null;
  }

  private getAdapter() {
    if (!this.adapter) {
      throw new Error('Auth client has not been initialized.');
    }

    return this.adapter;
  }

  private notify() {
    this.listeners.forEach((listener) => listener());
  }

  private setAdapter(adapter: AuthAdapter, provider: AuthProvider, options: { initialize?: boolean } = {}) {
    this.adapterUnsubscribe?.();
    this.adapter = adapter;
    this.provider = provider;
    this.adapterUnsubscribe = adapter.subscribe(() => {
      this.notify();
    });
    if (options.initialize !== false) {
      adapter.initialize?.();
    }
    this.notify();
  }

  private createAdapter(options: AuthInitOptions) {
    const provider = options.provider ?? resolveAuthProvider();

    if (provider === 'v2') {
      return new AuthV2Adapter();
    }

    return new LegacyAuthAdapter(options.endpoints);
  }

  private createProfileService() {
    return new UserService(this.endpoints ?? { baseUri: window.configs.AUTH_HOST }, false);
  }

  private async updateCurrentProfile(data: AuthProfileUpdatePayload): Promise<void> {
    const userService = this.createProfileService();

    await new Promise<void>((resolve, reject) => {
      userService.updateProfile(data, (error) => {
        if (error) {
          reject(error);
          return;
        }

        resolve();
      });
    });
  }
}

export default new AuthClient();
